import React, { useEffect, useRef, useState } from 'react'

import { FilterAccordion, useOnClickOutside, useOnKeyDown } from '@avantstay/backoffice-core'

import RadioField from '@new/__ui/RadioField'
import { RadioFieldOptionProps } from '@new/__ui/RadioField/_types'

import * as S from '../Filters.styles'
import { FiltersSharedProps } from '../Filters.types'

interface RadioBasedFieldProps<OptionValue extends string> extends FiltersSharedProps {
  title: string
  options: RadioFieldOptionProps<OptionValue>[]
  radioGroupId: string
  initialValue?: OptionValue
  defaultValue?: OptionValue
  onChange: (value?: OptionValue) => void
  shouldShowClearButton?: boolean
}

export const RadioBasedField = <OptionValue extends string>({
  title,
  radioGroupId,
  initialValue,
  defaultValue,
  options,
  isAccordionOpen,
  isSidebarFilter,
  isInlineFilter,
  onChange,
  onShowFilterAsColumn,
  showFilterAsColumn,
  shouldShowClearButton,
}: RadioBasedFieldProps<OptionValue>): JSX.Element => {
  const [selectedOption, setSelectedOption] = useState<OptionValue | undefined>(initialValue)

  const wrapperRef = useRef<HTMLDivElement>(null)

  const enterWasPressed = useOnKeyDown('enter')

  const handleSubmitValue = () => {
    if (selectedOption !== initialValue) {
      onChange(selectedOption)
    }
  }

  useOnClickOutside(wrapperRef, handleSubmitValue)

  useEffect(() => {
    if (!isSidebarFilter) {
      handleSubmitValue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enterWasPressed, isSidebarFilter])

  useEffect(() => {
    if (isSidebarFilter) {
      handleSubmitValue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, isSidebarFilter])

  // This clears selection when we run propertiesListStore.resetFilters
  useEffect(() => {
    if (initialValue === undefined || initialValue === defaultValue) {
      setSelectedOption(defaultValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue])

  const handleOnClear = () => {
    onChange(undefined)
    setSelectedOption(undefined)
  }

  const filterValue = options.find(option => option.value === selectedOption)?.title

  const renderFilter = () => (
    <RadioField.Group
      label={radioGroupId}
      onChange={e => setSelectedOption(e.currentTarget.value as OptionValue)}
      value={selectedOption}
      options={options}
    />
  )

  return (
    <FilterAccordion
      title={title}
      onClearFilter={handleOnClear}
      ref={wrapperRef}
      filterValue={filterValue}
      isAccordionOpen={isAccordionOpen}
      isSidebarFilter={isSidebarFilter}
      shouldDisableClearButton={selectedOption === defaultValue}
      shouldShowClearButton={shouldShowClearButton}
      showFilterAsColumn={showFilterAsColumn}
      onShowFilterAsColumn={onShowFilterAsColumn}
    >
      {isInlineFilter ? (
        <S.RadioFieldInlineContainer>{renderFilter()}</S.RadioFieldInlineContainer>
      ) : (
        renderFilter()
      )}
    </FilterAccordion>
  )
}
